import type { PostStatus, ScheduledPost } from '../../lib/api';
import { toZonedInput } from '../../lib/timezone';
import type { ListKind, Tab } from './postRows';

/**
 * What the schedule of posts and stories says, as pure functions over the
 * `['posts']` list (Murmura M1, PLAN.md §2): the Posts and Stories lists, and
 * the dashboard's "Prochaine mise en ligne" and "À traiter", read it the same way.
 *
 * A copy on a Facebook Page (D-143) rides on its post while they share a tab:
 * one item, not two. `posts` is the list the caller works on (one kind for a
 * list page, everything for the dashboard).
 */

/** Labels are `posts.tabs.<key>`; the last tab is "Échecs & annulées" so cancelled items have a home (R15). */
export const TABS: Array<{ key: Tab; statuses: PostStatus[] }> = [
  { key: 'upcoming', statuses: ['scheduled', 'publishing'] },
  { key: 'drafts', statuses: ['draft'] },
  { key: 'published', statuses: ['published'] },
  { key: 'failed', statuses: ['failed', 'canceled'] },
];

/** Where each list's composer is, and which posts it lists. */
export const KINDS: Record<ListKind, { newTo: string; editTo: (id: string) => string; isOf: (post: ScheduledPost) => boolean }> = {
  post: { newTo: '/posts/new', editTo: (id) => `/posts/${id}/edit`, isOf: (post) => post.type !== 'story' },
  story: { newTo: '/stories/new', editTo: (id) => `/stories/${id}/edit`, isOf: (post) => post.type === 'story' },
};

export const EDITABLE: readonly PostStatus[] = ['draft', 'scheduled', 'failed', 'canceled'];

/** Which list a post belongs to (D-137). */
export const kindOf = (post: ScheduledPost): ListKind => (post.type === 'story' ? 'story' : 'post');

export const tabOf = (post: ScheduledPost): Tab => TABS.find((entry) => entry.statuses.includes(post.status))!.key;

/** The tab `?tab=` asks for (Q23: "Voir" opens "Échecs & annulées"); anything else opens Upcoming. */
export function tabFromParam(value: string | null): Tab {
  return TABS.find((entry) => entry.key === value)?.key ?? 'upcoming';
}

/** The post's copy (D-143) while it rides on the post's row: both in the same tab. */
export function copyOf(posts: readonly ScheduledPost[], post: ScheduledPost): ScheduledPost | null {
  const copied = post.crossPost ? posts.find((p) => p.id === post.crossPost!.id) : undefined;
  return copied && tabOf(copied) === tabOf(post) ? copied : null;
}

/** A copy shown on its post's row rather than on its own. */
export function isRiding(posts: readonly ScheduledPost[], post: ScheduledPost): boolean {
  const owner = post.crossPostOfId ? posts.find((p) => p.id === post.crossPostOfId) : undefined;
  return Boolean(owner && copyOf(posts, owner)?.id === post.id);
}

/**
 * Retry is for real failures only: a cancelled one was stopped on purpose (A08-01).
 * A canceled campaign is final; a held one publishes nothing until launched (D-094).
 */
export const canRetry = (post: ScheduledPost): boolean => post.status === 'failed' && (!post.campaign || post.campaign.status === 'active');

/** Where a post opens: a copy whose post can still be edited is edited there (D-143); a published one opens read-only. */
export function openTarget(posts: readonly ScheduledPost[], post: ScheduledPost, editTo: (id: string) => string): string | null {
  if (post.status === 'published') return editTo(post.id);
  if (!EDITABLE.includes(post.status) || post.campaign?.status === 'canceled') return null;
  const owner = post.crossPostOfId ? posts.find((p) => p.id === post.crossPostOfId) : undefined;
  return editTo(owner && EDITABLE.includes(owner.status) ? owner.id : post.id);
}

/** What goes live next, soonest first: scheduled items with a time, a riding copy counted once. */
export function nextScheduled(posts: readonly ScheduledPost[]): ScheduledPost[] {
  return posts
    .filter((p) => p.status === 'scheduled' && p.scheduledAt !== null && !isRiding(posts, p))
    .sort((a, b) => Date.parse(a.scheduledAt!) - Date.parse(b.scheduledAt!));
}

/** Failed items (not cancelled: A08-01), a riding copy counted once, by list. */
export function failedOf(posts: readonly ScheduledPost[]): Record<ListKind, ScheduledPost[]> {
  const failed = posts.filter((p) => p.status === 'failed' && !isRiding(posts, p));
  return { post: failed.filter((p) => kindOf(p) === 'post'), story: failed.filter((p) => kindOf(p) === 'story') };
}

/** The calendar day of an instant on the zone's clock, "2027-03-16" (D-146): never the browser's day. */
export function dayKey(date: Date, zone: string): string {
  return toZonedInput(date, zone).slice(0, 10);
}

/**
 * What else is scheduled on a day of the zone ("Ce jour-là", Murmura M2,
 * D-187), by list: scheduled items with a time on that day. Left out: the ids
 * of `exclude` (the post being edited and its copy) and copies riding on their
 * post. A draft of a campaign not launched has a time but is not scheduled: it
 * does not count.
 */
export function sameDay(
  posts: readonly ScheduledPost[],
  { day, zone, exclude }: { day: string; zone: string; exclude: readonly string[] },
): Record<ListKind, number> {
  const onDay = posts.filter(
    (p) =>
      p.status === 'scheduled' &&
      p.scheduledAt !== null &&
      !exclude.includes(p.id) &&
      !isRiding(posts, p) &&
      dayKey(new Date(p.scheduledAt), zone) === day,
  );
  return { post: onDay.filter((p) => kindOf(p) === 'post').length, story: onDay.filter((p) => kindOf(p) === 'story').length };
}

/**
 * The composer's header stats (Q19, D-187): its kind's Drafts and Upcoming,
 * counted exactly as the list's tabs (`PostsPage`): items of the kind, a copy
 * riding on its post once, Upcoming = scheduled + publishing.
 */
export function draftAndUpcomingCounts(posts: readonly ScheduledPost[], kind: ListKind): { drafts: number; upcoming: number } {
  const everything = posts.filter(KINDS[kind].isOf);
  const all = everything.filter((p) => !isRiding(everything, p));
  const countOf = (key: Tab) => {
    const { statuses } = TABS.find((entry) => entry.key === key)!;
    return all.filter((p) => statuses.includes(p.status)).length;
  };
  return { drafts: countOf('drafts'), upcoming: countOf('upcoming') };
}

export interface UntimedCampaign {
  id: string;
  name: string;
  /** Its drafts with no planned time: launching refuses them (QUESTIONS Q2). */
  count: number;
}

/**
 * Draft campaigns whose posts still lack a time, in list order (PROPOSALS M1:
 * "Draft campaigns with items that have no time"). Paused campaigns are left
 * out, as the spec says draft (QUESTIONS.md, "Noted in M1").
 */
export function untimedByCampaign(posts: readonly ScheduledPost[]): UntimedCampaign[] {
  const groups = new Map<string, UntimedCampaign>();
  for (const post of posts) {
    if (post.status !== 'draft' || post.scheduledAt || post.campaign?.status !== 'draft' || isRiding(posts, post)) continue;
    const group = groups.get(post.campaign.id) ?? { id: post.campaign.id, name: post.campaign.name, count: 0 };
    groups.set(post.campaign.id, { ...group, count: group.count + 1 });
  }
  return [...groups.values()];
}
